// ./src/utils/api/CouponTypeService.ts
//
// Coupon type (account-level reusable "voucher" definitions — e.g. "Free Drink",
// "Drink + Snack") service. OpResult-native. Account-owned: every read is scoped
// to the caller's account and only the caller's own types are editable (see the
// `rights` field). The `items` menu (what a voucher gives) is edited in one shot
// via the nested `items` input, diffed on item id server-side. A multi-id
// update/remove is a bulk op (server restricts a bulk update to status).
// See em-scaffold-ui.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { listAllPages, ListAllOptions } from "./listAllPages";
import { Pagination, SortItem } from "./commonApiTypes";

// =============================================================================
// GraphQL
// =============================================================================

const COUPON_TYPE_ITEM_FIELDS = `
  id
  couponTypeId
  name
  quantity
  unitCost
  currencyCode
  isOptional
  inventoryItemId
  orderNo
  status
`;

// Exported so other services can embed a voucher without re-listing its fields
// (e.g. TicketTypeCoupon.couponType, which the grants editor renders).
export const COUPON_TYPE_FIELDS = `
  id
  accountId
  eventId
  name
  description
  color
  icon
  maxRedemptions
  couponValue
  validFrom
  validTo
  orderNo
  settings
  status
  rights
  items { ${COUPON_TYPE_ITEM_FIELDS} }
  createdAt
  updatedAt
`;

const COUPON_TYPE_LIST_QUERY = gql`
  query CouponTypeList($filter: CouponTypeFilter, $params: PaginationAndSorting) {
    couponTypeList(filter: $filter, params: $params) {
      code
      data { ${COUPON_TYPE_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const COUPON_TYPE_GET_QUERY = gql`
  query CouponTypeGet($id: ID) {
    couponTypeGet(id: $id) {
      code
      data { ${COUPON_TYPE_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const COUPON_TYPE_GET_MANY_QUERY = gql`
  query CouponTypeGetMany($ids: [ID]) {
    couponTypeGetMany(ids: $ids) {
      code
      data { ${COUPON_TYPE_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const COUPON_TYPE_CREATE_MUTATION = gql`
  mutation CouponTypeCreate($params: CouponTypeInput) {
    couponTypeCreate(params: $params) {
      code
      data { ${COUPON_TYPE_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const COUPON_TYPE_UPDATE_MUTATION = gql`
  mutation CouponTypeUpdate($where: CouponTypeWhereInput, $params: CouponTypeInput) {
    couponTypeUpdate(where: $where, params: $params) {
      code
      data { ${COUPON_TYPE_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const COUPON_TYPE_REMOVE_MUTATION = gql`
  mutation CouponTypeRemove($where: CouponTypeWhereInput) {
    couponTypeRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

// =============================================================================
// Types (match the eventsmagic-api CouponType schema)
// =============================================================================

export interface CouponTypeItem {
  id?: string;
  couponTypeId?: string | null;
  /** e.g. "Coffee", "Popcorn". */
  name?: string | null;
  quantity?: number | null;
  /** Vendor charge per unit (reconciliation basis). */
  unitCost?: number | null;
  currencyCode?: string | null;
  /** Staff-selectable at redemption (checkbox). */
  isOptional?: boolean | null;
  /** Soft ref to a tracked inventory item; redeeming this line draws that stock down (null = not tracked). */
  inventoryItemId?: string | null;
  orderNo?: number | null;
  status?: number;
}

export interface CouponType {
  id?: string;
  accountId?: string | null;
  /** Owning event, or null for an account-wide reusable type. */
  eventId?: string | null;
  name?: string | null;
  description?: string | null;
  color?: string | null;
  icon?: string | null;
  /** How many times ONE coupon of this type may be used. */
  maxRedemptions?: number | null;
  /** Flat amount owed to the vendor per redemption when there are no priced items. */
  couponValue?: number | null;
  validFrom?: string | null;
  validTo?: string | null;
  orderNo?: number | null;
  settings?: Record<string, unknown> | null;
  status?: number;
  /** Actions the current caller may perform ('edit' | 'remove' | 'duplicate'). Empty = read-only. */
  rights?: string[] | null;
  /** The menu of what this voucher gives. */
  items?: CouponTypeItem[] | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface CouponTypeItemInput {
  /** Set to update an existing item; omit to create a new one (nested diff). */
  id?: string | null;
  name?: string | null;
  quantity?: number | null;
  unitCost?: number | null;
  currencyCode?: string | null;
  isOptional?: boolean | null;
  inventoryItemId?: string | null;
}

export interface CouponTypeInput {
  name?: string | null;
  description?: string | null;
  color?: string | null;
  icon?: string | null;
  maxRedemptions?: number | null;
  couponValue?: number | null;
  validFrom?: string | null;
  validTo?: string | null;
  orderNo?: number | null;
  settings?: Record<string, unknown> | null;
  status?: number | null;
  /** When provided, replaces the whole item menu (diffed on item id server-side). */
  items?: CouponTypeItemInput[] | null;
  /**
   * Authorization context (NOT stored): the event page the caller is acting from. Lets crew with
   * tickets.manage on it create/edit a voucher under the event's account. Omit on the Library page.
   */
  contextEventId?: string | null;
}

// A bulk update (>1 id) may only mass-change status (server-enforced).
export interface CouponTypeBulkInput {
  status?: number | null;
}

export interface CouponTypeFilter {
  id?: string[] | null;
  eventId?: string[] | null;
  /** Restrict to account-wide reusable types (event_id IS NULL). */
  reusableOnly?: boolean | null;
  status?: number[] | null;
  searchKeyword?: string | null;
  /**
   * Authorization/scoping context (NOT a column): the event page the list is shown on. Scopes reads to
   * that event's account for authorized crew (tickets.manage), so a co_host sees the account's vouchers.
   */
  contextEventId?: string | null;
}

export interface CouponTypeWhereInput {
  /** One id, or many for a bulk update/remove. */
  id?: string | string[];
  /** Authorization context for a crew remove — the event page the delete is issued from. NOT stored. */
  contextEventId?: string | null;
}

// =============================================================================
// Service
// =============================================================================

export class CouponTypeService {
  static async list(
    filter?: CouponTypeFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    return ApiService.executeQuery(COUPON_TYPE_LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  /**
   * Fetches every coupon type matching `filter` by paging through the list.
   *
   * ⚠ IT EXISTS BECAUSE A PRINT PAGE MAY NOT CAP ITS ROWS (ROADMAP A5, `check-print-sheets` check 1).
   * The coupon print chooser needs the WHOLE voucher catalog to build its type picker — a bounded fetch
   * there means a type that simply is not offered, with nothing on screen saying the list was cut, which
   * is the same class of failure as a door list that stops at 500 names. ⚠ Mirrors `CouponService.listAll`.
   */
  static async listAll(
    filter?: CouponTypeFilter,
    sortBy?: SortItem[],
    opts?: ListAllOptions
  ): Promise<OpResult> {
    return listAllPages<CouponType>(CouponTypeService.list, filter, sortBy, opts);
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(COUPON_TYPE_GET_QUERY, { id });
  }

  static async getMany(ids: string[]): Promise<OpResult> {
    return ApiService.executeQuery(COUPON_TYPE_GET_MANY_QUERY, { ids });
  }

  static async create(params: CouponTypeInput): Promise<OpResult> {
    return ApiService.executeMutation(COUPON_TYPE_CREATE_MUTATION, { params });
  }

  static async update(where: CouponTypeWhereInput, params: CouponTypeInput): Promise<OpResult> {
    return ApiService.executeMutation(COUPON_TYPE_UPDATE_MUTATION, { where, params });
  }

  static async remove(id: string, contextEventId?: string | null): Promise<OpResult> {
    return ApiService.executeMutation(COUPON_TYPE_REMOVE_MUTATION, {
      where: { id, ...(contextEventId ? { contextEventId } : {}) },
    });
  }

  /** Mass-change status on every selected type in one call (server restricts a multi-id update to status). */
  static async bulkUpdate(ids: string[], params: CouponTypeBulkInput, contextEventId?: string | null): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(COUPON_TYPE_UPDATE_MUTATION, {
      where: { id: ids },
      params: { ...params, ...(contextEventId ? { contextEventId } : {}) },
    });
  }

  /** Remove every selected type in one call. */
  static async removeMany(ids: string[], contextEventId?: string | null): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(COUPON_TYPE_REMOVE_MUTATION, {
      where: { id: ids, ...(contextEventId ? { contextEventId } : {}) },
    });
  }
}
